import React from 'react';
import ModalContainer from "Components/Common/Modal/ModalContainer";
import BorderedButton from "Components//Common/components/Button/BorderedButton";
import ModalHeader from "Components//Common/Modal/ModalHeader";
import ModalBody from "Components//Common/Modal/ModalBody";
import BorderlessButton from "Components//Common/components/Button/BorderlessButton";
import ModalFooter from "Components//Common/Modal/ModalFooter";
import FillButton from "Components/Common/components/Button/FillButton";
import { ResetIcon } from "assests/GmsIcons";
import { Draggable, DraggableChildrenFn, OnDragEndResponder } from "react-beautiful-dnd";
import { TTableHeader } from "types/Components/Common/Table/Table";
import { useState } from "react";
import DragAndDropContainer from "Components/Common/components/DragAndDrop/DragAndDropContainer";
import DragAndDropHeaderItem from "./DragAndDropHeaderItem";
import * as types from "types/Components/Common/Table/TableHeaderControlModal";

/**
 * Modal to manage headers of table organize and hide/display header
 * columns. All changes are persisted locally, only on save will 
 * save the changes globally.
 * 
 * @param props.headers - current header properties
 * @param props.defaultHeaders  - default headers to reset
 * @param props.setHeaderControlOpen - function to toggle on/off modal state(for closing)
 * @param props.handleSave - save function new headers passed as param
 * @param props.headerControlOpen - state of modal on/off
 * @returns 
 */
const TableHeaderControlModal: types.TTableHeaderControlModal = ({
    headers, 
    defaultHeaders, 
    setHeaderControlOpen, 
    handleSave, 
    headerControlOpen, 
}) => {
    const [_headers, _setHeaders] = useState<TTableHeader[]>(JSON.parse(JSON.stringify(headers)))
    const [selected, setSelected] = useState<number[]>([])
    const [selectAll, setSelectAll] = useState<boolean>(false)

    let draggableComponent: (className?:string) => DraggableChildrenFn;

    // set display to false on all headers
    const handleRemoveAll: types.ThandleRemoveAll  = (e) => {
        e.preventDefault()

        _setHeaders((prevHeaders) => {
            let nextHeaders = [...prevHeaders]

            nextHeaders.map((header) => {
                header.display = false
            })
            return nextHeaders
        })
    }

    // handle checkbox changes
    const handleSelect: types.ThandleSelect = (e, id) => {
        if(e.target.checked) return setSelected(prevSelected => ([...prevSelected, id]))
        setSelected(prevSelected => {
            let nextSelected = [...prevSelected]

            const index = nextSelected.indexOf(id)
            nextSelected.splice(index, 1)

            return nextSelected
        })
    }

    // set display to true for selected headers
    const handleAdd: types.ThandleAdd = (e) => {
        _setHeaders((prevHeaders) => {
            let nextHeaders = [...prevHeaders]

            nextHeaders.map((header, index) => {
                if(selected.includes(header.id as number) || selectAll) {
                    // this is to ensure space-y does not effect hidden elements
                    const element = document.querySelector(`div[data-rbd-draggable-id="columnArrange${index}"]`) as HTMLDivElement
                    if(element) element.hidden = false
                    header.display = true
                }
            })
            return nextHeaders
        })

        setSelected([])
        setSelectAll(false)
    }

    // set display to false index header
    const handleRemove: types.ThandleRemove = (e, index) => {
        e.preventDefault()

        const element = document.querySelector(`div[data-rbd-draggable-id="columnArrange${index}"]`) as HTMLDivElement
        // hidden elements doesn't get effected with space-y
        if(element) element.hidden = true

        _setHeaders(prevHeaders => {
            let nextHeaders = [...prevHeaders]
            nextHeaders[index].display = false
            return nextHeaders
        })
    }

    // toggle select all button
    const handleSelectAll: types.ThandleSelectAll = (e) => {
        e.preventDefault()
        setSelectAll((prev) => !prev)
    }

    // reset headers to default headers
    const handleResetDefault: types.ThandleResetDefault = (e) => {
        e.preventDefault()
        _setHeaders(JSON.parse(JSON.stringify(defaultHeaders.current)))
    }

    /**
     * Change order of the headers on drag and drop
     */
    const onDragEnd: OnDragEndResponder = ({source, destination}) => {
        if(source.index == null || destination?.index == null) return
        _setHeaders((prevHeaders) => {
            let temp = [...prevHeaders]
            const removedHeader = temp.splice(source.index, 1)[0]
            if(!removedHeader) return prevHeaders
            const nextHeaders = [
                ...temp.slice(0, destination.index),
                removedHeader,
                ...temp.slice(destination.index)
            ]
            return nextHeaders
        })
    }

    // save changes and close modal
    const handleUpdate:types.ThandleUpdate = (e) =>{
        e.preventDefault()
        handleSave(_headers)
        setHeaderControlOpen(false)
    }

    draggableComponent = DragAndDropHeaderItem(_headers, handleRemove)

    return(
    <ModalContainer
        isOpen = {headerControlOpen}
        setOpen={() => setHeaderControlOpen(false)}
        >
        {/* header */}
        <ModalHeader>
            <div className={`tw-flex tw-justify-between tw-items-center`}>
            <h3 className={`tw-font-bold`}>Show/Hide Column</h3>
            <BorderedButton onClick={handleResetDefault} type="button"> {/* todo */}
                    <span className={`tw-flex tw-items-center tw-space-x-2`}>
                    <ResetIcon/>
                    <span>Reset to default</span>
                </span>
            </BorderedButton>
            </div>
        </ModalHeader>

        <ModalBody>
            <div className={`tw-flex tw-justify-between tw-space-x-7`}>
                {/* left */}
                <div className={`tw-w-[330px]`}>
                    <div className={`tw-flex tw-justify-between tw-items-center`}>
                        <span className={`tw-font-bold tw-text-slategray`}>Available</span>
                        <BorderlessButton onClick={handleSelectAll} type="button">{/* todo */}
                            <span>Select All</span>
                        </BorderlessButton> 
                    </div>

                    <div className={`tw-w-full tw-border tw-rounded-sm tw-h-[319px] tw-min-w-max tw-p-2 tw-space-y-2 tw-overflow-auto`}>
                        {
                            _headers.filter((header) => !header.display).map((header, key) => (
                                <div key = {key} className={`tw-flex tw-items-center tw-space-x-3 tw-p-1.5 tw-px-2.5`}>
                                    <input type="checkbox" 
                                        onChange={(e) => handleSelect(e, header.id as number)} 
                                        checked={(selected?.includes(header.id as number) || selectAll)}
                                        />
                                    <p className={`tw-leading-5 tw-max-w-[220px] tw-truncate`}>{header.name}</p>
                                </div>
                            ))
                        }
                    </div>
                </div>
                {/* center */}
                <div className={`tw-flex tw-items-center tw-w-min`}>
                    <BorderedButton onClick={handleAdd} type="button"> {/* todo */}
                        Add
                    </BorderedButton>
                </div>

                {/* right - drag and drop contents */}
                <div className={`tw-w-[330px] tw-relative`}>
                    <div className={`tw-flex tw-justify-between tw-items-center`}>
                        <span className={`tw-font-bold tw-text-slategray`}>Selected</span>
                        <BorderlessButton onClick={handleRemoveAll} type="button">{/* todo */}
                            <span>Remove All</span>
                        </BorderlessButton> 
                    </div>

                    <div className={`tw-max-w-full`}>
                        <DragAndDropContainer id = "table-header-dnd" onDragEnd = {onDragEnd}  renderItem = {draggableComponent("")}>
                            {
                                _headers.map((header, key) =>  (
                                <Draggable key={"columnArrange" + key} index={key} draggableId={"columnArrange" + key}>
                                    {
                                        (provided, snapshot, rubric) => {
                                            return draggableComponent(!header.display ? "tw-hidden" : undefined)(provided, snapshot, rubric)
                                        }
                                    }
                                </Draggable>
                            ))}
                        </DragAndDropContainer>
                    </div>
                </div>
            </div>
        </ModalBody>
        
        {/* footer */}
        <ModalFooter>
            <div className={`tw-flex tw-justify-end tw-mb-4`}>
                <div className={`tw-flex tw-space-x-3 `}>
                    <BorderedButton onClick={() => setHeaderControlOpen(false)} type="button">{/* todo */}
                        Cancel
                    </BorderedButton> 
                    <FillButton onClick={handleUpdate} type="button"> {/* todo */}
                        Update
                    </FillButton>
                </div>
            </div>
        </ModalFooter>
    </ModalContainer>
    )
}

export default TableHeaderControlModal
